// /auth/reset — the platform-hosted "click a link, set a new password" page.
//
// The counterpart of the forgot-password email. Cognito only does CODES
// (`ForgotPassword` emails a 6-digit code; there is no native magic link), so
// the s3p default template embeds that code inside a link to THIS page —
// `https://s3p.dev/auth/reset#slug=…&region=…&client=…&email=…&code=NNNNNN`.
// The user clicks once and types only a new password; the code rides along in
// the fragment.
//
// WHY ONE PLATFORM-HOSTED PAGE (not one per app): a forgot-password email is
// sent by the app's OWN Cognito pool, but the person clicking it is
// unauthenticated and the app is arbitrary developer code we don't control.
// The only thing we need to complete the reset is region + the app's PUBLIC
// spa clientId + the email + the code — all of which the sending runtime bakes
// into the fragment. So this page needs no config lookup, no CORS, no session:
// it reads the fragment and calls `ConfirmForgotPassword` on the named client
// directly. Same reasoning (and same fragment-not-query safety) as /auth/adopt
// and /auth/connect.
//
// After a successful reset we DON'T try to carry a session across origins —
// we send the user back to their app's own sign-in to log in fresh.

import {
  Alert,
  Anchor,
  Button,
  Card,
  Group,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconLock, IconShieldCheck } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { createAuth } from "/sdk/index.js";

interface ResetParams {
  slug: string;
  region: string;
  client: string;
  email: string;
  code: string;
}

/** Read the reset params from the #fragment and ERASE it from the address bar
 *  in the same breath — the code is a credential and shouldn't linger in a
 *  screenshot or the page URL. (History is still history; see /auth/adopt.) */
function takeParamsFromFragment(): ResetParams | null {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;
  const p = new URLSearchParams(hash);
  const params: ResetParams = {
    slug: p.get("slug") ?? "",
    region: p.get("region") ?? "",
    client: p.get("client") ?? "",
    email: p.get("email") ?? "",
    code: p.get("code") ?? "",
  };
  // Wipe the fragment (keep the path) so the code leaves the visible URL.
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  if (!params.region || !params.client || !params.email || !params.code) return null;
  return params;
}

export function AuthReset(): JSX.Element {
  // Read ONCE on mount (the fragment is wiped immediately after).
  const params = useMemo(() => takeParamsFromFragment(), []);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // The app the user is resetting for — where we send them to sign in after.
  const appUrl = params?.slug ? `https://${params.slug}.s3p.dev` : "/signin";

  useEffect(() => {
    if (!params) {
      setError(
        "This reset link is missing or incomplete — request a new one from the app's sign-in page.",
      );
    }
  }, [params]);

  async function submit(): Promise<void> {
    if (!params) return;
    if (password.length < 8) {
      setError("Choose a password of at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // A throwaway auth module bound to the app's OWN pool client (from the
      // link). No session adoption — onSession is a no-op; we redirect to the
      // app to sign in fresh.
      const auth = createAuth({
        region: params.region,
        clientId: params.client,
        onSession: () => {},
      });
      await auth.confirmForgotPassword(params.email, params.code, password);
      setDone(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Cognito's raw messages are usable; sharpen the common expiry case.
      setError(
        /expired|CodeMismatch|ExpiredCode/i.test(msg)
          ? "That reset link has expired or was already used. Request a new one from the app's sign-in page."
          : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack align="center" pt={80} px="md">
      <Card withBorder radius="md" padding="xl" maw={460} w="100%">
        <Group gap="xs" mb="sm">
          <IconLock size={22} />
          <Title order={2}>Choose a new password</Title>
        </Group>

        {done ? (
          <Alert color="teal" icon={<IconShieldCheck />} title="Password updated">
            Your password has been changed. You can now{" "}
            <Anchor href={appUrl}>sign in{params?.slug ? ` to ${params.slug}` : ""}</Anchor> with it.
          </Alert>
        ) : !params ? (
          <Alert color="red" title="Can't reset">
            {error}
          </Alert>
        ) : (
          <Stack>
            <Text size="sm" c="dimmed">
              Setting a new password for <b>{params.email}</b>.
            </Text>
            <PasswordInput
              label="New password"
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoFocus
            />
            <PasswordInput
              label="Confirm new password"
              placeholder="Type it again"
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && void submit()}
            />
            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button
                onClick={() => void submit()}
                loading={busy}
                disabled={!password || !confirm}
              >
                Set new password
              </Button>
            </Group>
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
